/**
 * EquipmentCreateModal — the "Add Item" form for the equipment catalogue (#333).
 *
 * Deliberately four fields. Creating an item is a small act; everything else an
 * item carries — physical facts, role ladders, suppliers, hired-in — is edited
 * afterwards on /equipment/:id, where each section is full width and commits
 * itself. This replaces the create half of the old EquipmentEditForm, which
 * asked for all of it behind one Save button.
 *
 * "Adding another?" keeps the modal open after a successful create so a delivery of
 * new stock can be typed in one sitting; unchecked, the caller navigates to the
 * new item's detail page.
 */
import { useEffect, useMemo, useState } from "react";
import { cx } from "@shared/cx";
import { Button } from "../ui/Button";
import { Toggle } from "../ui/Toggle";
import forms from "../ui/field.module.css";
import styles from "./EquipmentCreateModal.module.css";
import { createEquipment } from "../../lib/equipment-api";
import { useDismissable } from "../../lib/use-dismissable";
import { useModalScrollLock } from "../../lib/use-modal-scroll-lock";
import { useMutationRunner } from "../../lib/use-mutation-runner";
import { validateEquipment } from "./validators";
import { InlineBanner } from "../ui/InlineBanner";

interface EquipmentCreateModalProps {
  isOpen: boolean;
  onClose: () => void;
  /**
   * Called after every successful create. `id` is the new row's id (null if the
   * response omitted one). `stayOpen` reports the "Adding another?" choice: true
   * means refresh the list and leave the modal up; false means the modal has
   * closed and the caller should navigate to the new item.
   */
  onCreated: (id: string | null, stayOpen: boolean) => void;
}

export function EquipmentCreateModal({
  isOpen,
  onClose,
  onCreated,
}: EquipmentCreateModalProps) {
  const [name, setName] = useState("");
  const [rate, setRate] = useState("");
  // Most catalogue rows are a single unit; pre-filling 1 saves the common case.
  const [qty, setQty] = useState("1");
  const [location, setLocation] = useState("");
  const [addAnother, setAddAnother] = useState(false);
  // Modal-local runner: the error renders inline (a page-level banner would sit
  // behind the overlay).
  const mutations = useMutationRunner();
  const saving = mutations.isPending("create-equipment");
  // Reveal-then-live: hidden until first submit, then recomputed every render.
  const [showValidation, setShowValidation] = useState(false);

  // Only the three scalar keys — passing the physical-fact keys would collect
  // phantom errors for fields this form does not have.
  const fieldErrors = useMemo(
    () => validateEquipment({ name, rate, qty }),
    [name, rate, qty],
  );

  const resetFields = () => {
    setName("");
    setRate("");
    setQty("1");
    setLocation("");
    setShowValidation(false);
  };

  useEffect(() => {
    if (!isOpen) return;
    resetFields();
    setAddAnother(false);
    mutations.setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mutations.setError is stable per render; depending on `mutations` would re-run on every pending change
  }, [isOpen]);

  // Lock background scroll while open (DESIGN-SYSTEM.md §Modals) and close on Escape.
  // The dismissal stack keeps Escape on the topmost surface, so an open Select
  // or a layered ConfirmModal closes first.
  useModalScrollLock(isOpen);
  useDismissable({ open: isOpen, onDismiss: onClose, pointer: "none" });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (Object.keys(fieldErrors).length > 0) {
      setShowValidation(true);
      return;
    }

    await mutations.run("create-equipment", "Add item", async () => {
      const id = await createEquipment({
        name: name.trim(),
        hire_rate_per_day: Number(rate),
        stock_quantity: Number(qty),
        storage_location: location.trim() || null,
      });
      if (addAnother) {
        resetFields();
        onCreated(id, true);
        return;
      }
      onClose();
      onCreated(id, false);
    });
  };

  if (!isOpen) return null;

  return (
    <div
      className={styles.overlay}
      onClick={onClose}
    >
      <div
        className={styles.panel}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className={styles.heading}>Add item</h2>
        <p className={styles.description}>
          Roles, suppliers, and dimensions are set on the item afterwards.
        </p>
        <form onSubmit={handleSubmit} className={styles.form}>
          <div>
            <label
              className={forms.label}
              htmlFor="equipment-create-name"
            >
              Name
            </label>
            <input
              id="equipment-create-name"
              type="text"
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              aria-invalid={
                (showValidation && Boolean(fieldErrors.name)) || undefined
              }
              className={cx(
                forms.input,
                showValidation && fieldErrors.name && forms.invalid,
              )}
            />
            {showValidation && fieldErrors.name && (
              <InlineBanner variant="error">{fieldErrors.name}</InlineBanner>
            )}
          </div>
          <div className={styles.pair}>
            <div>
              <label
                className={forms.label}
                htmlFor="equipment-create-rate"
              >
                Hire rate / day
              </label>
              <input
                id="equipment-create-rate"
                type="text"
                inputMode="decimal"
                value={rate}
                onChange={(e) => setRate(e.target.value)}
                aria-invalid={
                  (showValidation && Boolean(fieldErrors.hire_rate)) ||
                  undefined
                }
                className={cx(
                  forms.input,
                  showValidation && fieldErrors.hire_rate && forms.invalid,
                )}
              />
              {showValidation && fieldErrors.hire_rate && (
                <InlineBanner variant="error">
                  {fieldErrors.hire_rate}
                </InlineBanner>
              )}
            </div>
            <div>
              <label
                className={forms.label}
                htmlFor="equipment-create-qty"
              >
                Stock quantity
              </label>
              <input
                id="equipment-create-qty"
                type="text"
                inputMode="numeric"
                value={qty}
                onChange={(e) => setQty(e.target.value)}
                aria-invalid={
                  (showValidation && Boolean(fieldErrors.stock_quantity)) ||
                  undefined
                }
                className={cx(
                  forms.input,
                  showValidation &&
                    fieldErrors.stock_quantity &&
                    forms.invalid,
                )}
              />
              {showValidation && fieldErrors.stock_quantity && (
                <InlineBanner variant="error">
                  {fieldErrors.stock_quantity}
                </InlineBanner>
              )}
            </div>
          </div>
          <div>
            <label
              className={forms.label}
              htmlFor="equipment-create-location"
            >
              Storage spot{" "}
              <span className={forms.optional}>(optional)</span>
            </label>
            <input
              id="equipment-create-location"
              type="text"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              className={forms.input}
            />
          </div>
          {mutations.error && (
            <p className={styles.error}>{mutations.error}</p>
          )}
          <div className={styles.footer}>
            <div className={styles.stayOpen}>
              {/* aria-label, not aria-labelledby: the Astryx Switch doesn't
                  forward labelledby to its input (same note as SettingRow). */}
              <Toggle
                checked={addAnother}
                onChange={setAddAnother}
                aria-label="Adding another?"
              />
              <span className={styles.stayOpenLabel}>Adding another?</span>
            </div>
            <Button type="button" variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" loading={saving}>
              Add item
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
